import { CheckmarkCircle, X as XIcon } from '@ui/components/icons';
import { useDataChainId } from '@ui/hooks/datachain';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { wsConfig } from '@ui/lib/wallet/config';
import TradingOneClickV2Facet from '@ui/services/contract/abis/TradingOneClickV2Facet';
import { getContractAddress } from '@ui/services/contract/constants';
import { Zap } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Box, Flex, HStack, styled, VStack } from 'styled-system/jsx';
import { stringToHex, zeroAddress } from 'viem';
import { useAccount, usePublicClient, useSignMessage } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';

import { isAddressInOneClickAllowlist } from './constants/allowlist';
import { useCheckOneClickTradingStatus } from './hooks/useCheckOneClickTradingStatus';
import {
  createAndStoreOneClickWalletForOwner,
  deleteOneClickWalletRecord,
} from './oneClickWalletHelper';
import { useOneClickTradingModalStore } from './store/useOneClickTradingModalStore';
import { useOneClickTradingStore } from './store/useOneClickTradingStore';

export const OneClickTradingModal = () => {
  const { t } = useTranslation();
  const { address, isConnected } = useAccount();
  const isOpen = useOneClickTradingModalStore((s) => s.isOpen);
  const close = useOneClickTradingModalStore((s) => s.close);
  const created = useOneClickTradingStore((s) => s.created);
  const enabled = useOneClickTradingStore((s) => s.enabled);
  const hasCheckedStatus = useOneClickTradingStore((s) => s.hasCheckedStatus);
  const oneClickWalletAddress = useOneClickTradingStore(
    (s) => s.oneClickWalletAddress,
  );
  const setStateFromWallet = useOneClickTradingStore(
    (s) => s.setStateFromWallet,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { signMessageAsync } = useSignMessage();

  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });

  const { writeContractAsync, isPending } = useTxnWriteContract({});
  const { simulate, isSimulating } = useSimulateMutation();

  const { update: updateStatus } = useCheckOneClickTradingStatus();
  const isAllowlistedForOneClick = isAddressInOneClickAllowlist(address);
  const isComingSoonRestricted = !isAllowlistedForOneClick;

  const handleCreateOneClickWallet = async () => {
    if (!isConnected || !address) return;

    try {
      setIsCreating(true);

      const record = await createAndStoreOneClickWalletForOwner({
        ownerAddress: address,
        signMessageAsync,
      });

      setStateFromWallet({
        created: true,
        hasCheckedStatus: true,
        owner: address,
        oneClickWalletAddress: record.oneClickWalletAddress as any,
      });

      void updateStatus();
    } catch {
      // ignore errors for now; could hook into toast/bugsnag later
    } finally {
      setIsCreating(false);
    }
  };

  const handleEnableDelegation = async () => {
    if (!isConnected || !address || !oneClickWalletAddress) return;

    const diamondAddress = getContractAddress('LeverUp', chainId);
    if (!diamondAddress || !publicClient) return;

    try {
      setIsEnabling(true);

      const ALL_PERMISSIONS = 2n ** 256n - 1n;
      const agentName = stringToHex('LeverUp 1CT', { size: 32 });

      const { isSuccess } = await simulate(async () => {
        await publicClient.estimateContractGas({
          abi: TradingOneClickV2Facet,
          address: diamondAddress,
          functionName: 'authorizeAgent',
          account: address,
          args: [oneClickWalletAddress, agentName, ALL_PERMISSIONS],
        });
      });

      if (!isSuccess) return;

      const txHash = await writeContractAsync({
        abi: TradingOneClickV2Facet,
        address: diamondAddress,
        functionName: 'authorizeAgent',
        args: [oneClickWalletAddress, agentName, ALL_PERMISSIONS],
      });

      await waitForTransactionReceipt(wsConfig, {
        hash: txHash,
        chainId,
      });

      void updateStatus();
    } catch {
      // ignore errors for now; could hook into toast/bugsnag later
    } finally {
      setIsEnabling(false);
    }
  };

  const handleResetOneClickTrading = async () => {
    if (!isConnected || !address) return;

    const diamondAddress = getContractAddress('LeverUp', chainId);
    if (!diamondAddress || !publicClient) return;

    try {
      setIsResetting(true);

      // 1. Call contract to revoke agent authorization
      const { isSuccess } = await simulate(async () => {
        await publicClient.estimateContractGas({
          abi: TradingOneClickV2Facet,
          address: diamondAddress,
          functionName: 'revokeAgent',
          account: address,
          args: [oneClickWalletAddress!],
        });
      });

      if (!isSuccess) return;

      const txHash = await writeContractAsync({
        abi: TradingOneClickV2Facet,
        address: diamondAddress,
        functionName: 'revokeAgent',
        args: [oneClickWalletAddress!],
      });

      await waitForTransactionReceipt(wsConfig, {
        hash: txHash,
        chainId,
      });

      // 2. Delete local 1CT wallet record (only after onchain confirmation)
      await deleteOneClickWalletRecord(address);

      void updateStatus();
    } catch {
      // ignore errors for now; could hook into toast/bugsnag later
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      modal={false}
      open={isOpen}
      type="desktopModal_mobileDrawer"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            p="0"
            color="v2.text.secondary"
            minW={{ base: 'full', tablet: '430px' }}
            maxW={{ base: 'full', tablet: '430px' }}
            position="relative"
          >
            <Dialog.Title asChild>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                p="14px 16px"
                borderBottom="1px solid"
                borderColor="v2.border.default"
              >
                <HStack alignItems="center" gap="8px">
                  <Icon
                    boxSize="16px"
                    color="v2.long.default"
                    flexShrink={0}
                  >
                    <Zap strokeWidth={1.5} />
                  </Icon>
                  <Text
                    fontSize="15px"
                    fontWeight="500"
                    lineHeight="20px"
                    color="v2.text.primary"
                  >
                    {t('common.Enable Gasless 1-Click Trading')}
                  </Text>
                </HStack>
                <Dialog.CloseTrigger
                  boxShadow="none"
                  bg="none"
                  onClick={() => close()}
                  position="relative"
                  zIndex={isComingSoonRestricted ? 30 : undefined}
                >
                  <Icon w="20px" h="20px">
                    <XIcon />
                  </Icon>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Title>

            <Dialog.Description>
              <VStack alignItems="stretch" gap="12px" pt="16px">
                <Box px="16px">
                  <Text
                    textStyle="v2.ui"
                    fontWeight="400"
                    lineHeight="1.55"
                    color="v2.text.secondary"
                    py={1}
                  >
                    Enable gasless transactions and 1-click trading for Open
                    Market Orders and Close Positions. And enjoy instant trading
                    and 1001x leverage profit.
                  </Text>
                  <Text
                    textStyle="v2.label"
                    fontWeight="400"
                    color="v2.text.muted"
                    py={1}
                  >
                    Assets always remain in your own wallet, with no top-ups
                    required.
                  </Text>
                  <Text
                    textStyle="v2.meta"
                    fontWeight="400"
                    color="v2.text.subtle"
                    py={1}
                  >
                    *Open Position with native token (MON) is not supported for
                    1CT.
                  </Text>
                </Box>

                <Text
                  px="16px"
                  textStyle="v2.sectionLabel"
                  color="v2.text.subtle"
                >
                  {t('common.Steps', 'STEPS')}
                </Text>

                <VStack px="16px" gap="8px" alignItems="stretch">
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    bg="v2.bg.surface1"
                    border="1px solid"
                    borderColor="v2.border.default"
                    borderRadius="4px"
                    p="12px 14px"
                  >
                    <HStack gap="12px" alignItems="center">
                      <Flex
                        w="22px"
                        h="22px"
                        flexShrink={0}
                        bg="v2.bg.surface2"
                        border="1px solid"
                        borderColor="v2.border.default"
                        borderRadius="2px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text textStyle="numbers.xsmall" color="v2.text.muted">
                          1
                        </Text>
                      </Flex>
                      <VStack gap="2px" alignItems="flex-start">
                        <Text
                          textStyle="v2.tableHeader"
                          color="v2.text.secondary"
                        >
                          SECURE 1CT WALLET
                        </Text>
                        <Text
                          textStyle="v2.meta"
                          fontWeight="400"
                          color="v2.text.subtle"
                        >
                          Secure your 1CT wallet that only exists on your
                          device.
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      variant="primary"
                      disabled={
                        isComingSoonRestricted ||
                        !hasCheckedStatus ||
                        !isConnected ||
                        created ||
                        isCreating
                      }
                      loading={
                        isComingSoonRestricted
                          ? false
                          : !hasCheckedStatus || isCreating || isResetting
                      }
                      onClick={handleCreateOneClickWallet}
                    >
                      {created ? (
                        <Icon w="18px" h="18px" color="v2.long.default">
                          <CheckmarkCircle />
                        </Icon>
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </HStack>
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    bg="v2.bg.surface1"
                    border="1px solid"
                    borderColor="v2.border.default"
                    borderRadius="4px"
                    p="12px 14px"
                  >
                    <HStack gap="12px" alignItems="center">
                      <Flex
                        w="22px"
                        h="22px"
                        flexShrink={0}
                        bg="v2.bg.surface2"
                        border="1px solid"
                        borderColor="v2.border.default"
                        borderRadius="2px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text textStyle="numbers.xsmall" color="v2.text.muted">
                          2
                        </Text>
                      </Flex>
                      <VStack gap="2px" alignItems="flex-start">
                        <Text
                          textStyle="v2.tableHeader"
                          color="v2.text.secondary"
                        >
                          {t(
                            'oneClickTrading.enableDelegation.title',
                            'ENABLE ACCOUNT DELEGATION',
                          )}
                        </Text>
                        <Text
                          textStyle="v2.meta"
                          fontWeight="400"
                          color="v2.text.subtle"
                        >
                          {t(
                            'oneClickTrading.enableDelegation.desc',
                            'Delegate signatures to your 1CT wallet.',
                          )}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      variant="primary"
                      disabled={
                        isComingSoonRestricted ||
                        !hasCheckedStatus ||
                        !isConnected ||
                        !created ||
                        enabled ||
                        isEnabling ||
                        isSimulating ||
                        isPending
                      }
                      loading={
                        isComingSoonRestricted
                          ? false
                          : !hasCheckedStatus ||
                            isEnabling ||
                            isSimulating ||
                            isPending
                      }
                      onClick={handleEnableDelegation}
                    >
                      {enabled ? (
                        <Icon w="18px" h="18px" color="v2.long.default">
                          <CheckmarkCircle />
                        </Icon>
                      ) : (
                        t('common.Enable', 'Enable')
                      )}
                    </Button>
                  </HStack>
                </VStack>

                <HStack
                  px="16px"
                  pb="16px"
                  justifyContent="space-between"
                  mt="4px"
                >
                  <Box>
                    {hasCheckedStatus && created && enabled && (
                      <Button
                        variant="secondary"
                        bg="transparent"
                        color="v2.short.default"
                        borderColor="v2.short.border"
                        _hover={{
                          bg: 'v2.short.dim',
                          color: 'v2.short.default',
                          borderColor: 'v2.short.border',
                        }}
                        disabled={
                          isComingSoonRestricted ||
                          isResetting ||
                          isSimulating ||
                          isPending
                        }
                        loading={isComingSoonRestricted ? false : isResetting}
                        onClick={handleResetOneClickTrading}
                      >
                        {t('common.Reset')}
                      </Button>
                    )}
                  </Box>
                  <Button
                    variant={
                      hasCheckedStatus && created && enabled
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={close}
                  >
                    {hasCheckedStatus && created && enabled
                      ? t('common.Confirm')
                      : t('common.Close')}
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Description>
            {isComingSoonRestricted && (
              <Box
                position="absolute"
                top="49px"
                left="0"
                right="0"
                bottom="65px"
                bg="rgba(10, 11, 10, 0.85)"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="8px"
                zIndex={20}
                pointerEvents="auto"
                backdropFilter="blur(4px)"
                px="24px"
                textAlign="center"
              >
                <Text
                  fontSize="15px"
                  fontWeight="600"
                  color="v2.text.primary"
                  letterSpacing="0.01em"
                >
                  {t('common.Coming Soon', 'Coming Soon')}
                </Text>
                <Text
                  textStyle="v2.label"
                  fontWeight="400"
                  color="v2.text.muted"
                >
                  {t(
                    'oneClickTrading.comingSoon.desc',
                    '1CT beta access is currently limited to allowlisted wallets.',
                  )}
                </Text>
              </Box>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
